import React, { useState, useEffect } from "react";
import {
  Table,
  Empty,
  Row,
  Col,
  Drawer,
  notification,
  Popconfirm,
  Button,
  Tooltip,
  Spin,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  BarsOutlined,
  RightCircleOutlined,
} from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";
import { useHistory } from "react-router-dom";
import moment from "moment";
import RequestHandler from "../../util/RequestHandler";
import useSweepAccess from "../../util/useSweepAccess";
import SweepRuleListFilter from "../Filters/SweepRuleListFilter";

// EnumSweepRuleFrequencyPeriod: Day = 1, Week = 2, Month = 3
// OData enum'ları string üye adı olarak da serialize edebilir (Day/Week/Month)
const buildFrequencyText = (interval, period) => {
  if (!interval) return "";
  const periodText =
    period === 1 || period === "Day"
      ? "günde bir"
      : period === 2 || period === "Week"
      ? "haftada bir"
      : "ayda bir";
  return `Her ${interval} ${periodText}`;
};

// EnumSweepRuleEndType: Never = 0, AfterOccurrence = 1, OnDate = 2
// OData enum'ları string üye adı olarak da serialize edebilir (Never/AfterOccurrence/OnDate)
const buildEndText = (record) => {
  switch (record.endType) {
    case 0:
    case "Never":
      return "Sonlanmasın";
    case 1:
    case "AfterOccurrence":
      return `${record.endAfterOccurrences ?? ""} tekrardan sonra`;
    case 2:
    case "OnDate":
      return record.endDate ? moment(record.endDate).format("DD.MM.YYYY") : "";
    default:
      return "";
  }
};

const SweepRuleList = () => {
  const history = useHistory();
  const { loading: accessLoading, hasAccess } = useSweepAccess();
  const isDesktopOrLaptop = useMediaQuery({
    query: "(min-device-width: 1224px)",
  });

  const [data, setData] = useState({
    data: [],
    total: 0,
    loading: true,
  });
  const [take, setTake] = useState(20);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [visibleDetail, setVisibleDetail] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const reList = () => {
    setData((prev) => ({ ...prev, loading: true }));
    RequestHandler.get(
      `/SweepRule?$count=true&$skip=${skip}&$top=${take}${
        filter ? `&$filter=${filter}` : ""
      }`
    ).then((response) => {
      if (response && Array.isArray(response.value)) {
        setData({
          data: response.value,
          total: response["@odata.count"],
          loading: false,
        });
      } else {
        setData({ data: [], total: 0, loading: false });
      }
    });
  };

  useEffect(() => {
    reList();
  }, [skip, take, filter]);

  const onFinishTableFilter = (query, values) => {
    setSkip(0);
    setFilterValues(values);
    setFilter(query);
  };

  const onClearTableFilter = () => {
    setSkip(0);
    setFilterValues({});
    setFilter("");
  };

  const showDetail = (record) => {
    setDetailRows([]);
    setVisibleDetail(true);
    setDetailLoading(true);
    RequestHandler.get(`/SweepRule/GetById/${record.id}`).then((response) => {
      setDetailRows((response && response.details) || []);
      setDetailLoading(false);
    });
  };

  const handleDelete = (id) => {
    RequestHandler.delete(`/SweepRule/${id}`).then((response) => {
      if (response && response.success) {
        reList();
      } else {
        notification.error({
          message: "HATA",
          description: "Silme işlemi sırasında bir hata oluştu.",
          duration: 2,
        });
      }
    });
  };

  const paging = (p) => {
    setSkip((p.current - 1) * p.pageSize);
    setTake(p.pageSize);
  };

  const columns = [
    {
      title: "Tanım Adı",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Alıcı Banka",
      dataIndex: "receiverBankName",
      key: "receiverBankName",
    },
    {
      title: "Alıcı Hesap",
      dataIndex: "receiverIban",
      key: "receiverIban",
    },
    {
      title: "Gerçekleşme Saati",
      dataIndex: "executionTime",
      key: "executionTime",
      render: (text) => {
        if (!text) return "";
        const dur = moment.duration(text);
        const h = String(Math.floor(dur.asHours())).padStart(2, "0");
        const m = String(dur.minutes()).padStart(2, "0");
        return `${h}:${m}`;
      },
    },
    {
      title: "Tekrar Sıklığı",
      key: "frequencyText",
      render: (text, record) =>
        buildFrequencyText(record.frequencyInterval, record.frequencyPeriod),
    },
    {
      title: "Sonlanma",
      key: "endText",
      render: (text, record) => buildEndText(record),
    },
    {
      title: "Gönderen Hesaplar",
      key: "detail",
      align: "center",
      render: (text, record) => (
        <Tooltip title="Gönderen Hesap Detayı">
          <a
            onClick={() => showDetail(record)}
            style={{ color: "#1890ff", fontSize: "130%" }}
          >
            <RightCircleOutlined />
          </a>
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "edit",
      align: "right",
      render: (text, record) => (
        <a
          onClick={() => history.push(`/tos-sweep-rule/edit/${record.id}`)}
          style={{ color: "orange", fontSize: "130%" }}
        >
          <EditOutlined />
        </a>
      ),
    },
    {
      title: "",
      key: "delete",
      render: (text, record) => (
        <Popconfirm
          title="Silmek istiyor musunuz?"
          okText="Evet"
          cancelText="Hayır"
          onConfirm={() => handleDelete(record.id)}
        >
          <a style={{ color: "red", fontSize: "130%" }}>
            <DeleteOutlined />
          </a>
        </Popconfirm>
      ),
    },
  ];

  const detailColumns = [
    { title: "Banka", dataIndex: "bankName", key: "bankName" },
    {
      title: "Gönderen Hesap",
      key: "senderAccount",
      render: (text, record) =>
        `${record.senderAccountName ? record.senderAccountName + " - " : ""}${
          record.senderIban || record.senderAccountNumber || ""
        }`,
    },
    { title: "Açıklama", dataIndex: "description", key: "description" },
    {
      title: "Gönderilecek Tutar",
      dataIndex: "amountToSend",
      key: "amountToSend",
      render: (text, record) =>
        record.sendAllBalance ? "Tüm Bakiye" : text,
    },
    {
      title: "Min. Kalan Bakiye",
      dataIndex: "minRemainingBalance",
      key: "minRemainingBalance",
      render: (text, record) =>
        record.sendAllBalance ? "—" : text ?? "",
    },
    { title: "Para Birimi", dataIndex: "currencyCode", key: "currencyCode" },
  ];

  if (accessLoading || !hasAccess) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Row>
        <Col span={18}>
          <p style={{ fontSize: "120%", marginTop: -9 }}>
            <BarsOutlined /> NAKİT SÜPÜRME KURAL TANIMI
          </p>
        </Col>
        <Col span={6} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            onClick={() => history.push("/tos-sweep-rule/add")}
          >
            <PlusOutlined /> Süpürme Kuralı Ekle
          </Button>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <SweepRuleListFilter
            data={filterValues}
            onFinishFilter={onFinishTableFilter}
            onClearFilter={onClearTableFilter}
            style={{ marginTop: 12, marginBottom: 12 }}
          />
        </Col>
      </Row>

      <Drawer
        title="Gönderen Hesaplar"
        width={isDesktopOrLaptop ? 900 : "100%"}
        onClose={() => setVisibleDetail(false)}
        visible={visibleDetail}
        destroyOnClose
      >
        <Table
          columns={detailColumns}
          dataSource={detailRows}
          rowKey="id"
          size="small"
          loading={detailLoading}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Veri Yok"
              />
            ),
          }}
        />
      </Drawer>

      <Row>
        <Col span={24}>
          <Table
            columns={columns}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Veri Yok"
                />
              ),
            }}
            style={{ overflowX: "auto", maxHeight: "100%" }}
            size="small"
            rowKey="id"
            onChange={paging}
            pagination={{
              pageSizeOptions: ["15", "25", "50", "100"],
              defaultPageSize: take,
              total: data.total,
            }}
            dataSource={data.data}
            loading={data.loading}
          />
        </Col>
      </Row>
    </div>
  );
};

export default SweepRuleList;
